import React,{useState} from 'react';
import {Link,useLocation} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiMenu,FiX,FiChevronDown,FiPhone}=FiIcons;

const Header=({currentArea})=> {
  const [isMenuOpen,setIsMenuOpen]=useState(false);
  const [isCommercialDropdownOpen,setIsCommercialDropdownOpen]=useState(false);
  const [isDomesticDropdownOpen,setIsDomesticDropdownOpen]=useState(false);
  const location=useLocation();

  const handleLinkClick=()=> {
    setIsMenuOpen(false);
    setIsCommercialDropdownOpen(false);
    setIsDomesticDropdownOpen(false);
    window.scrollTo({top: 0,behavior: 'smooth'});
  };

  // Format area name for URL (convert spaces to hyphens and lowercase) 
  const formatAreaForUrl=(area)=> {
    return area.toLowerCase().replace(/\s+/g,'-');
  };

  const areaUrlParam=formatAreaForUrl(currentArea);

  // Helper function to get area-aware link
  const getAreaLink=(basePath)=> {
    if (currentArea==='Aylesbury') {
      return basePath;
    }
    return `${basePath}/${areaUrlParam}`;
  };

  // Helper function to check if a link is active
  const isLinkActive=(path)=> {
    const currentPath=location.pathname;
    
    // Handle home page
    if (path==='/') {
      return currentPath==='/' || currentPath.startsWith('/home/');
    }
    
    // Handle area-specific pages
    if (currentArea!=='Aylesbury') {
      const areaSpecificPath=`${path}/${areaUrlParam}`;
      return currentPath===areaSpecificPath || currentPath===path;
    }
    
    // Handle regular pages - also check if current path starts with the base path
    return currentPath===path || currentPath.startsWith(path + '/');
  };

  // Helper function to check if dropdown section is active
  const isDropdownSectionActive=(basePath,subPages)=> {
    const currentPath=location.pathname;
    
    // Check if main section is active
    if (isLinkActive(basePath)) {
      return true;
    }
    
    // Check if any sub-page is active
    return subPages.some(subPage=> {
      const subPagePath=currentArea==='Aylesbury' ? subPage.path : `${subPage.path}/${areaUrlParam}`;
      return currentPath===subPagePath || currentPath.startsWith(subPage.path + '/');
    });
  };

  // Helper function to get active link classes
  const getLinkClasses=(path)=> {
    const baseClasses="text-gray-700 hover:text-blue-600 font-medium transition-colors";
    const activeClasses="text-blue-600 font-semibold";
    
    return isLinkActive(path) ? `${baseClasses} ${activeClasses}` : baseClasses;
  };

  // Helper function to get dropdown link classes (for main dropdown buttons)
  const getDropdownLinkClasses=(basePath,subPages)=> {
    const baseClasses="text-gray-700 hover:text-blue-600 font-medium transition-colors";
    const activeClasses="text-blue-600 font-semibold";
    
    return isDropdownSectionActive(basePath,subPages) ? `${baseClasses} ${activeClasses}` : baseClasses;
  };

  // Helper function to get dropdown item classes
  const getDropdownItemClasses=(subPagePath)=> {
    const currentPath=location.pathname;
    const fullSubPagePath=currentArea==='Aylesbury' ? subPagePath : `${subPagePath}/${areaUrlParam}`;
    
    const isActive=currentPath===fullSubPagePath || currentPath.startsWith(subPagePath + '/');
    
    return isActive 
      ? "block px-4 py-3 hover:bg-gray-50 transition-colors bg-blue-50" 
      : "block px-4 py-3 hover:bg-gray-50 transition-colors";
  };

  // Helper function to get dropdown item text classes
  const getDropdownItemTextClasses=(subPagePath)=> {
    const currentPath=location.pathname;
    const fullSubPagePath=currentArea==='Aylesbury' ? subPagePath : `${subPagePath}/${areaUrlParam}`;
    
    const isActive=currentPath===fullSubPagePath || currentPath.startsWith(subPagePath + '/');
    
    return isActive 
      ? "text-sm font-semibold text-blue-600" 
      : "text-sm font-medium text-gray-900 hover:text-blue-600";
  };

  const commercialSubPages=[
    {title: 'Communal Cleaning',path: '/communal-cleaning',description: 'Apartments & Retirement Properties'},
    {title: 'Office Cleaning',path: '/office-cleaning',description: 'Professional Workspace Maintenance'},
    {title: 'Bars & Restaurants',path: '/bars-restaurants-cleaning',description: 'Food Service & Hospitality'}
  ];

  const domesticSubPages=[
    {title: 'Deep Cleaning',path: '/deep-cleaning',description: 'Comprehensive Home Deep Clean'},
    {title: 'End of Tenancy',path: '/end-of-tenancy',description: 'Deposit Back Guarantee'}
  ];

  const handleCommercialMouseEnter=()=> {
    setIsCommercialDropdownOpen(true);
    setIsDomesticDropdownOpen(false);
  };

  const handleCommercialMouseLeave=()=> {
    setIsCommercialDropdownOpen(false);
  };

  const handleDomesticMouseEnter=()=> {
    setIsDomesticDropdownOpen(true);
    setIsCommercialDropdownOpen(false);
  };

  const handleDomesticMouseLeave=()=> {
    setIsDomesticDropdownOpen(false);
  };

  return (
    <header className="bg-white shadow-lg fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 sm:py-5 md:py-6 lg:py-4">
          <div className="flex items-center mx-auto lg:mx-0">
            <Link to='/'>
              <img className='h-12 object-contain' src='/img/logo-cleaning.png'></img>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {/* Commercial Dropdown */}
            <div className="relative group" onMouseEnter={handleCommercialMouseEnter} onMouseLeave={handleCommercialMouseLeave}>
              <Link to={getAreaLink('/commercial')} className={`${getDropdownLinkClasses('/commercial',commercialSubPages)} flex items-center space-x-1 py-2`} onClick={handleLinkClick}>
                <span>Commercial</span>
                <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
              </Link>

              {/* Commercial Dropdown Menu */}
              <div className={`absolute top-full left-0 mt-0 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-2 transition-all duration-200 ${isCommercialDropdownOpen ? 'opacity-100 visible transform translate-y-0' : 'opacity-0 invisible transform -translate-y-2'}`}>
                <div className="px-4 py-2 border-b border-gray-100">
                  <Link to={getAreaLink('/commercial')} className={`block text-sm font-semibold transition-colors ${isLinkActive('/commercial') ? 'text-blue-600' : 'text-gray-900 hover:text-blue-600'}`} onClick={handleLinkClick}>
                    All Commercial Services
                  </Link>
                </div>
                {commercialSubPages.map((subPage,index)=> (
                  <Link key={index} to={currentArea==='Aylesbury' ? subPage.path : `${subPage.path}/${areaUrlParam}`} className={getDropdownItemClasses(subPage.path)} onClick={handleLinkClick}>
                    <div className={getDropdownItemTextClasses(subPage.path)}>
                      {subPage.title}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {subPage.description}
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Domestic Dropdown */}
            <div className="relative group" onMouseEnter={handleDomesticMouseEnter} onMouseLeave={handleDomesticMouseLeave}>
              <Link to={getAreaLink('/domestic')} className={`${getDropdownLinkClasses('/domestic',domesticSubPages)} flex items-center space-x-1 py-2`} onClick={handleLinkClick}>
                <span>Domestic</span>
                <SafeIcon icon={FiChevronDown} className="w-4 h-4" />
              </Link>

              {/* Domestic Dropdown Menu */}
              <div className={`absolute top-full left-0 mt-0 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-2 transition-all duration-200 ${isDomesticDropdownOpen ? 'opacity-100 visible transform translate-y-0' : 'opacity-0 invisible transform -translate-y-2'}`}>
                <div className="px-4 py-2 border-b border-gray-100">
                  <Link to={getAreaLink('/domestic')} className={`block text-sm font-semibold transition-colors ${isLinkActive('/domestic') ? 'text-blue-600' : 'text-gray-900 hover:text-blue-600'}`} onClick={handleLinkClick}>
                    All Domestic Services
                  </Link>
                </div>
                {domesticSubPages.map((subPage,index)=> (
                  <Link key={index} to={currentArea==='Aylesbury' ? subPage.path : `${subPage.path}/${areaUrlParam}`} className={getDropdownItemClasses(subPage.path)} onClick={handleLinkClick}>
                    <div className={getDropdownItemTextClasses(subPage.path)}>
                      {subPage.title}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {subPage.description}
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/contact" className={getLinkClasses('/contact')} onClick={handleLinkClick}>
              Contact
            </Link>

            <a href="tel:07878522378" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
              Call: 07878 522378
            </a>
          </nav>

          <div className="hidden lg:flex items-center">
            <Link to="/book" className={`px-6 py-2 rounded-lg transition-colors font-semibold ${isLinkActive('/book') ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`} onClick={handleLinkClick}>
              Book Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button className="lg:hidden p-2" onClick={()=> setIsMenuOpen(!isMenuOpen)}>
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6 text-gray-700" />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 py-4 bg-white">
            <nav className="flex flex-col space-y-4">
              {/* Commercial Section */}
              <div>
                <Link to={getAreaLink('/commercial')} className={`${getLinkClasses('/commercial')} block pb-2`} onClick={handleLinkClick}>
                  Commercial Services
                </Link>
                <div className="ml-4 space-y-2">
                  {commercialSubPages.map((subPage,index)=> (
                    <Link key={index} to={currentArea==='Aylesbury' ? subPage.path : `${subPage.path}/${areaUrlParam}`} className={`block text-sm transition-colors ${isLinkActive(subPage.path) ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-blue-600'}`} onClick={handleLinkClick}>
                      {subPage.title}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Domestic Section */}
              <div>
                <Link to={getAreaLink('/domestic')} className={`${getLinkClasses('/domestic')} block pb-2`} onClick={handleLinkClick}>
                  Domestic Services
                </Link>
                <div className="ml-4 space-y-2">
                  {domesticSubPages.map((subPage,index)=> (
                    <Link key={index} to={currentArea==='Aylesbury' ? subPage.path : `${subPage.path}/${areaUrlParam}`} className={`block text-sm transition-colors ${isLinkActive(subPage.path) ? 'text-blue-600 font-semibold' : 'text-gray-600 hover:text-blue-600'}`} onClick={handleLinkClick}>
                      {subPage.title}
                    </Link>
                  ))}
                </div>
              </div>

              <Link to="/contact" className={getLinkClasses('/contact')} onClick={handleLinkClick}>
                Contact
              </Link>

              {/* Mobile Phone Button - Now Red */}
              <a href="tel:07878522378" className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold text-center flex items-center justify-center space-x-2">
                <SafeIcon icon={FiPhone} className="w-4 h-4" />
                <span>Call: 07878 522378</span>
              </a>

              <Link to="/book" className={`px-6 py-2 rounded-lg transition-colors font-semibold text-center mt-4 ${isLinkActive('/book') ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`} onClick={handleLinkClick}>
                Book Now
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;